// Force-upgrade gate — port of Flutter's ForceUpgradeGate (forceUpgrader.dart).
//
// On every app start it asks Play what the live version is. If the installed
// build is behind, it renders a blocking "Update Required" screen INSTEAD of the
// app: there is no dismiss, no back, no way through except updating.
//
// It wraps the whole router, so the gate applies before login — the same place
// the Dart puts it (main.dart wraps SplashScreen).
//
// ── Deliberate behaviours, all inherited from the Dart ──────────────────────
//
// FAILS OPEN. If the lookup errors, times out (5 s) or the scrape finds no
// version, the app runs normally. This is not laziness: the version comes from
// scraping an undocumented page, and the alternative — failing closed — would
// lock every user out of their door locks and lights the moment Google changed
// their markup. A missed upgrade prompt is recoverable; a mass lockout is not.
//
// ANDROID ONLY. There is no store to check on web/desktop, so the gate passes
// straight through and never delays a browser session.
//
// BLOCKS STARTUP while checking (spinner, ≤5 s). Necessary rather than nice: if
// the app rendered first and the gate appeared after, a user on a slow network
// would get a usable app for a second — which is exactly what "cannot be used"
// is meant to prevent.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, useTheme,
} from '@mui/material';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import { App as CapApp } from '@capacitor/app';
import { checkStoreUpgrade, openPlayStore, isAndroid } from '../../core/ota/storeVersion';

export default function ForceUpgradeGate({ children }) {
  const theme = useTheme();
  const c = theme.sli || { back: '#101820', button: '#37474F', text: '#FFFFFF' };

  // Off-native there is nothing to check, so start already resolved — a browser
  // session must never wait on this.
  const [checked, setChecked] = useState(() => !isAndroid());
  const [info, setInfo] = useState(null);

  const run = useCallback(async (markChecked) => {
    try {
      const r = await checkStoreUpgrade();
      setInfo(r);
    } catch (e) {
      /* fail open */
    } finally {
      if (markChecked) setChecked(true);
    }
  }, []);

  useEffect(() => {
    if (!isAndroid()) return undefined;
    let handle;
    run(true);

    // Re-check when the app returns to the foreground. A phone cold-starts often
    // enough that mount-only would do, but a wall-mounted tablet can stay open
    // for weeks — and OtaUpdater already re-checks on resume, so without this the
    // web bundle would keep updating on a device whose native build is stale and
    // was never asked to upgrade.
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) run(false);
    }).then((h) => { handle = h; }).catch(() => {});

    return () => { try { handle && handle.remove(); } catch (e) { /* ignore */ } };
  }, [run]);

  if (!checked) {
    return (
      <Box sx={{
        height: '100%',
        bgcolor: c.back,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      >
        <CircularProgress sx={{ color: c.text }} />
      </Box>
    );
  }

  if (!info || !info.blocked) return children;

  return (
    <Box sx={{
      height: '100%',
      bgcolor: c.back,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
      textAlign: 'center',
    }}
    >
      <Box sx={{ maxWidth: 420 }}>
        <SystemUpdateIcon sx={{ fontSize: 80, color: c.text }} />

        <Typography sx={{
          mt: 2.5, fontSize: 28, fontWeight: 700, color: c.text,
        }}
        >
          Update Required
        </Typography>

        <Typography sx={{ mt: 1.5, fontSize: 16, color: c.text, opacity: 0.85 }}>
          A newer version of Smart Life India is available. Please update to
          continue using the app.
        </Typography>

        {info.store && (
          <Typography sx={{ mt: 2, fontSize: 13, color: c.text, opacity: 0.6 }}>
            {`Installed ${info.current} · Latest ${info.store}`}
          </Typography>
        )}

        <Button
          onClick={openPlayStore}
          sx={{
            mt: 4,
            bgcolor: c.button,
            color: c.text,
            px: 4,
            py: 1.5,
            borderRadius: '12px',
            fontSize: 16,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { bgcolor: c.button },
          }}
        >
          Update Now
        </Button>
      </Box>
    </Box>
  );
}
